import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Modal,
  Platform,
  Dimensions,
} from 'react-native';
import { useSelector } from 'react-redux';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '../../../../../theme/useTheme';
import Svg, { Path } from 'react-native-svg';
import LhTextInput from '../../../../../components/LhTextInput';
import LHModalPop from '../../../../../components/LHModalPop';
import LHConfirmationDialog from '../../../../../components/LHConfirmationDialog';
import CrossPlatformDropdown from '../../../../../components/CrossPlatformDropdown';
import FontFamily from '../../../../../constants/fonts';
import {
  Role,
  RoleFormModalProps,
  RoleFormValues,
  RoleAvailability,
  OrganizationType,
  ThreePLStatus,
  LaborRoleType,
  RoleStatus,
} from '../types';
import {
  selectLocTypes,
  selectOrgTypes,
  selectRoleTypes,
  selectThreePLOptions,
  selectDsdVpZones,
  selectNonDsdVpZones,
} from '../../../../../store/slices/masterDataSlice';

interface CheckboxProps {
  label: string;
  checked: boolean;
  onPress: () => void;
  disabled?: boolean;
}

const Checkbox: React.FC<CheckboxProps> = ({ label, checked, onPress, disabled = false }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  return (
  <TouchableOpacity
    style={styles.checkboxContainer}
    onPress={onPress}
    disabled={disabled}
    activeOpacity={0.7}
  >
    <View
      style={[
        styles.checkboxBox,
        checked && styles.checkboxBoxChecked,
        disabled && styles.checkboxBoxDisabled,
        isDark && !checked && !disabled && { backgroundColor: colors.input.background, borderColor: colors.input.border },
      ]}
    >
      {checked && <MaterialIcons name="check" size={16} color="#FFFFFF" />}
    </View>
    <Text style={[styles.checkboxLabel, disabled && styles.checkboxLabelDisabled, isDark && { color: colors.text.primary }]}>{label}</Text>
  </TouchableOpacity>
  );
};

interface ToggleSwitchProps {
  value: RoleStatus;
  onValueChange: (value: RoleStatus) => void;
  disabled?: boolean;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ value, onValueChange, disabled = false }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const isEnabled = value === 'Enabled';

  return (
    <TouchableOpacity
      style={styles.toggleContainer}
      onPress={() => {
        if (!disabled) {
          onValueChange(isEnabled ? 'Disabled' : 'Enabled');
        }
      }}
      disabled={disabled}
      activeOpacity={0.7}
    >
      <View
        style={[
          styles.toggleTrack,
          isEnabled ? styles.toggleTrackActive : styles.toggleTrackInactive,
          disabled && styles.toggleTrackDisabled,
          isDark && !isEnabled && { backgroundColor: '#555' },
        ]}
      >
        <View
          style={[
            styles.toggleThumb,
            isEnabled ? styles.toggleThumbActive : styles.toggleThumbInactive,
          ]}
        />
      </View>
      <Text style={[styles.toggleLabel, isDark && { color: colors.text.primary }]}>{isEnabled ? 'Enabled' : 'Disabled'}</Text>
    </TouchableOpacity>
  );
};

interface FormSectionProps {
  title: string;
  required?: boolean;
  children: React.ReactNode;
  error?: string;
}

const FormSection: React.FC<FormSectionProps> = ({ title, required, children, error }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  return (
  <View style={styles.formSection}>
    <View style={styles.sectionHeader}>
      <Text style={[styles.sectionTitle, isDark && { color: colors.text.primary }]}>{title}</Text>
      {required && <Text style={styles.requiredAsterisk}>*</Text>}
    </View>
    {children}
    {error && <Text style={styles.errorText}>{error}</Text>}
  </View>
  );
};

interface MultiSelectDropdownProps {
  label: string;
  placeholder: string;
  options: string[];
  selected: string[];
  required?: boolean;
  error?: string;
  disabled?: boolean;
  onChange: (values: string[]) => void;
}

/**
 * Custom multi-select dropdown per AC:
 * - Trigger shows "[X] selected" when items are chosen
 * - Dropdown opens with checkboxes + "Select All" / "Clear All" buttons
 * - Selected values display as removable tags below the trigger
 */
const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  placeholder,
  options,
  selected,
  required,
  error,
  disabled,
  onChange,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 260 });
  const triggerRef = useRef<any>(null);

  const handleOpen = () => {
    if (disabled) return;
    if (!triggerRef.current) { setOpen(true); return; }
    if (Platform.OS === 'web') {
      try {
        const rect = (triggerRef.current as any).getBoundingClientRect();
        const screenW = Dimensions.get('window').width;
        const dropW = Math.max(rect.width, 260);
        let left = rect.left;
        if (left + dropW > screenW - 8) left = Math.max(8, screenW - dropW - 8);
        setPos({ top: rect.bottom + 4, left, width: dropW });
      } catch { /* fallback */ }
    } else {
      try {
        (triggerRef.current as any).measureInWindow((x: number, y: number, w: number, h: number) => {
          const screenW = Dimensions.get('window').width;
          const dropW = Math.max(w, 260);
          let left = x;
          if (left + dropW > screenW - 8) left = Math.max(8, screenW - dropW - 8);
          setPos({ top: y + h + 4, left, width: dropW });
        });
      } catch { /* fallback */ }
    }
    setOpen(true);
  };

  const toggleItem = (item: string) => {
    if (selected.includes(item)) {
      onChange(selected.filter((s) => s !== item));
    } else {
      onChange([...selected, item]);
    }
  };

  const selectAll = () => onChange([...options]);
  const clearAll = () => onChange([]);

  const removeTag = (item: string) => {
    onChange(selected.filter((s) => s !== item));
  };

  const triggerText = selected.length === 0
    ? placeholder
    : `${selected.length} selected`;

  return (
    <View style={styles.formSection}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>{label}</Text>
        {required && <Text style={styles.requiredAsterisk}>*</Text>}
      </View>

      {/* Trigger */}
      <TouchableOpacity
        ref={triggerRef}
        onPress={handleOpen}
        activeOpacity={0.7}
        style={[
          styles.multiTrigger,
          disabled && styles.multiTriggerDisabled,
          error ? styles.multiTriggerError : undefined,
          isDark && { backgroundColor: colors.input.background, borderColor: colors.input.border },
        ]}
      >
        <Text style={[styles.multiTriggerText, selected.length === 0 && styles.multiTriggerPlaceholder, isDark && { color: selected.length === 0 ? colors.text.disabled : colors.text.primary }]}>
          {triggerText}
        </Text>
        <MaterialIcons name={open ? 'arrow-drop-up' : 'arrow-drop-down'} size={24} color={isDark ? colors.text.secondary : '#495057'} />
      </TouchableOpacity>

      {/* Removable tags */}
      {selected.length > 0 && (
        <View style={styles.tagWrap}>
          {selected.map((item) => (
            <View key={item} style={[styles.removableTag, isDark && { backgroundColor: '#0D2E5C', borderColor: '#1E5AA8' }]}>
              <Text style={[styles.removableTagText, isDark && { color: '#FFFFFF' }]}>{item}</Text>
              <TouchableOpacity onPress={() => removeTag(item)} disabled={disabled} hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}>
                <MaterialIcons name="close" size={14} color={isDark ? '#A0C4FF' : '#01346F'} />
              </TouchableOpacity>
            </View>
          ))}
        </View>
      )}

      {error && <Text style={styles.errorText}>{error}</Text>}

      {/* Dropdown via Modal */}
      {open && (
        <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
          <TouchableOpacity style={styles.msOverlay} activeOpacity={1} onPress={() => setOpen(false)}>
            <View style={[styles.msDropdown, { position: 'absolute', top: pos.top, left: pos.left, minWidth: pos.width }, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}>
              {/* Select All / Clear All */}
              <View style={[styles.msActions, isDark && { borderBottomColor: colors.border.divider }]}>
                <TouchableOpacity onPress={selectAll}>
                  <Text style={[styles.msActionText, isDark && { color: colors.brand.primary }]}>Select All</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={clearAll}>
                  <Text style={[styles.msActionText, isDark && { color: colors.brand.primary }]}>Clear All</Text>
                </TouchableOpacity>
              </View>
              {/* Options with checkboxes — scrollable */}
              <ScrollView
                style={styles.msOptionsList}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator={true}
                nestedScrollEnabled={true}
              >
                {options.map((opt) => {
                  const isChecked = selected.includes(opt);
                  return (
                    <TouchableOpacity
                      key={opt}
                      onPress={() => toggleItem(opt)}
                      activeOpacity={0.7}
                      style={[styles.msOption, isDark && { borderBottomColor: colors.border.divider }]}
                    >
                      <View style={[styles.msCheckbox, isChecked && styles.msCheckboxChecked, isDark && !isChecked && { backgroundColor: colors.input.background, borderColor: colors.input.border }]}>
                        {isChecked && (
                          <Svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3}>
                            <Path d="M20 6L9 17l-5-5" />
                          </Svg>
                        )}
                      </View>
                      <Text style={[styles.msOptionText, isDark && { color: colors.text.primary }]}>{opt}</Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </View>
          </TouchableOpacity>
        </Modal>
      )}
    </View>
  );
};

interface SingleSelectDropdownProps {
  label: string;
  placeholder: string;
  options: string[];
  value: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}

const SingleSelectDropdown: React.FC<SingleSelectDropdownProps> = ({
  label,
  placeholder,
  options,
  value,
  required,
  error,
  disabled,
  onChange,
}) => {
  const childList = options.map((opt) => ({ id: opt, displayText: opt }));
  const selectedValue = value ? { id: value, displayText: value } : null;

  return (
    <CrossPlatformDropdown
      childList={childList}
      label={label}
      placeholder={placeholder}
      selection="single"
      isRequired={required}
      isDisabled={disabled}
      isClearable={true}
      selectedValue={selectedValue as any}
      setSelectedValue={(selectedItem: any) => {
        onChange(selectedItem ? String(selectedItem.displayText) : '');
      }}
      helperText={error ?? ''}
      feedbackType={error ? 'error' : 'info'}
      size="small"
      disableSorting={true}
      labelStyle={styles.dropdownLabel}
      placeholderStyle={styles.dropdownPlaceholderText}
      style={styles.dropdownField}
      searchPlaceholder="Search..."
    />
  );
};

// Fallback constants — used only if master data has not yet been loaded from the API.
const FALLBACK_LOC_TYPES: string[] = ['Distribution', 'Production', 'Manufacturing'];
const FALLBACK_ORG_TYPES: OrganizationType[] = ['PBNA', 'FLNA', '1NA'];
const FALLBACK_ROLE_TYPES: LaborRoleType[] = ['DSD', 'Non-DSD', 'Fixed'];
const FALLBACK_THREE_PL_OPTIONS: ThreePLStatus[] = ['Yes', 'No', 'Any'];

export const RoleFormModal: React.FC<RoleFormModalProps> = ({
  visible,
  mode,
  initialValues,
  isSaving,
  onSave,
  onCancel,
  locationTypes,
  existingRoles,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  // ── Master data from Redux ────────────────────────────────────────────────
  const reduxLocTypes     = useSelector(selectLocTypes);
  const reduxOrgTypes     = useSelector(selectOrgTypes);
  const reduxRoleTypes    = useSelector(selectRoleTypes);
  const reduxThreePL      = useSelector(selectThreePLOptions);
  const reduxDsdVpZones   = useSelector(selectDsdVpZones);
  const reduxNonDsdVpZones = useSelector(selectNonDsdVpZones);

  // Use Redux data when available; fall back to hardcoded defaults.
  const locTypeOptions: string[]         = reduxLocTypes.length     ? reduxLocTypes     : FALLBACK_LOC_TYPES;
  const organizationTypeOptions: string[] = reduxOrgTypes.length    ? reduxOrgTypes     : FALLBACK_ORG_TYPES;
  const roleTypeOptions: string[]         = reduxRoleTypes.length   ? reduxRoleTypes    : FALLBACK_ROLE_TYPES;
  const threePlStatusOptions: string[]    = reduxThreePL.length     ? reduxThreePL      : FALLBACK_THREE_PL_OPTIONS;

  const [formValues, setFormValues] = useState<RoleFormValues>({
    name: '',
    availableIn: [],
    locTypes: [],
    status: 'Enabled',
    organizationTypes: [],
    threePlStatus: '',
    roleType: undefined,
    vpZones: [],
  });

  // Derive the correct VP zone options based on the currently selected role type.
  // Must be declared after formValues state to avoid "used before declaration" error.
  // Comparison is case-insensitive to handle API returning 'NON-DSD' vs form value 'Non-DSD'.
  const vpZoneOptions: string[] = useMemo(() => {
    const rt = (formValues.roleType ?? '').toUpperCase();
    if (rt === 'DSD') return reduxDsdVpZones;
    if (rt === 'NON-DSD') return reduxNonDsdVpZones;
    return [];
  }, [formValues.roleType, reduxDsdVpZones, reduxNonDsdVpZones]);

  const [errors, setErrors] = useState<Partial<Record<keyof RoleFormValues, string>>>({});
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);
  const [showLaborRemovalConfirm, setShowLaborRemovalConfirm] = useState(false);

  useEffect(() => {
    if (visible) {
      if (mode === 'edit' && initialValues) {
        setFormValues({
          name: initialValues.name,
          availableIn: initialValues.availableIn,
          locTypes: initialValues.locTypes,
          status: initialValues.status,
          organizationTypes: initialValues.organizationTypes ?? [],
          threePlStatus: initialValues.threePlStatus ?? '',
          roleType: initialValues.roleType,
          vpZones: initialValues.vpZones ?? [],
        });
      } else {
        setFormValues({
          name: '',
          availableIn: [],
          locTypes: [],
          status: 'Enabled',
          organizationTypes: [],
          threePlStatus: '',
          roleType: undefined,
          vpZones: [],
        });
      }
      setErrors({});
      setShowUnsavedConfirm(false);
      setShowLaborRemovalConfirm(false);
    }
  }, [visible, mode, initialValues]);

  const baselineFormValues = useMemo<RoleFormValues>(() => {
    if (mode === 'edit' && initialValues) {
      return {
        name: initialValues.name,
        availableIn: initialValues.availableIn,
        locTypes: initialValues.locTypes,
        status: initialValues.status,
        organizationTypes: initialValues.organizationTypes ?? [],
        threePlStatus: initialValues.threePlStatus ?? '',
        roleType: initialValues.roleType,
        vpZones: initialValues.vpZones ?? [],
      };
    }
    return {
      name: '',
      availableIn: [],
      locTypes: [],
      status: 'Enabled',
      organizationTypes: [],
      threePlStatus: '',
      roleType: undefined,
      vpZones: [],
    };
  }, [mode, initialValues]);

  const isDuplicateRoleName = (nameValue: string): boolean => {
    const normalized = nameValue.trim().toLowerCase();
    if (!normalized) return false;
    return (existingRoles ?? []).some((role: Role) => {
      if (mode === 'edit' && initialValues && role.name.trim().toLowerCase() === initialValues.name.trim().toLowerCase()) {
        return false;
      }
      return role.name.trim().toLowerCase() === normalized;
    });
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof RoleFormValues, string>> = {};

    if (!formValues.name.trim()) {
      newErrors.name = 'Role Name is required.';
    } else if (formValues.name.trim().length > 15) {
      newErrors.name = 'Role Name must not exceed 15 characters.';
    } else if (isDuplicateRoleName(formValues.name)) {
      newErrors.name = 'A role with this name already exists.';
    }

    if (formValues.availableIn.length === 0) {
      newErrors.availableIn = 'At least one module is required.';
    }

    if (formValues.locTypes.length === 0) {
      newErrors.locTypes = 'Location Type is required.';
    }

    if (formValues.organizationTypes.length === 0) {
      newErrors.organizationTypes = 'Organization Type is required.';
    }

    if (!formValues.threePlStatus) {
      newErrors.threePlStatus = '3PL Status is required.';
    }

    if (formValues.availableIn.includes('Labor Optimization') && !formValues.roleType) {
      newErrors.roleType = 'Role Type is required.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const hasUnsavedChanges = useMemo(() => {
    const sortList = (arr: string[]) => [...arr].sort();

    return (
      formValues.name.trim() !== baselineFormValues.name.trim() ||
      JSON.stringify(sortList(formValues.availableIn)) !== JSON.stringify(sortList(baselineFormValues.availableIn)) ||
      JSON.stringify(sortList(formValues.locTypes)) !== JSON.stringify(sortList(baselineFormValues.locTypes)) ||
      JSON.stringify(sortList(formValues.organizationTypes)) !== JSON.stringify(sortList(baselineFormValues.organizationTypes)) ||
      formValues.threePlStatus !== baselineFormValues.threePlStatus ||
      formValues.roleType !== baselineFormValues.roleType ||
      formValues.status !== baselineFormValues.status ||
      JSON.stringify(sortList(formValues.vpZones ?? [])) !== JSON.stringify(sortList(baselineFormValues.vpZones ?? []))
    );
  }, [baselineFormValues, formValues]);

  const canSave = useMemo(() => {
    if (isSaving) return false;
    if (!formValues.name.trim() || formValues.name.trim().length > 15) return false;
    if (isDuplicateRoleName(formValues.name)) return false;
    if (formValues.availableIn.length === 0) return false;
    if (formValues.locTypes.length === 0) return false;
    if (formValues.organizationTypes.length === 0) return false;
    if (!formValues.threePlStatus) return false;
    if (formValues.availableIn.includes('Labor Optimization') && !formValues.roleType) return false;
    return true;
  }, [formValues, isSaving]);

  const clearError = (field: keyof RoleFormValues) => {
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleNameChange = (text: string) => {
    // Enforce maxLength at the application level (browser maxlength may not
    // cover all edge cases such as IME composition or programmatic updates)
    const capped = text.length > 15 ? text.slice(0, 15) : text;
    setFormValues((prev) => ({ ...prev, name: capped }));
    // Real-time inline duplicate & length checks
    if (capped.trim() && capped.trim().length > 15) {
      setErrors((prev) => ({ ...prev, name: 'Role Name must not exceed 15 characters.' }));
    } else if (capped.trim() && isDuplicateRoleName(capped)) {
      setErrors((prev) => ({ ...prev, name: 'A role with this name already exists.' }));
    } else {
      clearError('name');
    }
  };

  const toggleAvailableIn = (value: RoleAvailability) => {
    const isRemovingLabor = value === 'Labor Optimization' &&
      formValues.availableIn.includes('Labor Optimization') &&
      !!formValues.roleType;

    if (isRemovingLabor) {
      setShowLaborRemovalConfirm(true);
      return;
    }

    setFormValues((prev) => {
      const nextAvailableIn = prev.availableIn.includes(value)
        ? prev.availableIn.filter((v) => v !== value)
        : [...prev.availableIn, value];

      return {
        ...prev,
        availableIn: nextAvailableIn,
        roleType: nextAvailableIn.includes('Labor Optimization') ? prev.roleType : undefined,
        // Clear VP zones when Labor Optimization is removed
        vpZones: nextAvailableIn.includes('Labor Optimization') ? (prev.vpZones ?? []) : [],
      };
    });

    clearError('availableIn');
    clearError('roleType');
  };

  const setLocTypes = (values: string[]) => {
    setFormValues((prev) => ({ ...prev, locTypes: values }));
    clearError('locTypes');
  };

  const setOrganizationTypes = (values: string[]) => {
    setFormValues((prev) => ({ ...prev, organizationTypes: values as OrganizationType[] }));
    clearError('organizationTypes');
  };

  const setThreePlStatus = (value: string) => {
    setFormValues((prev) => ({ ...prev, threePlStatus: value as ThreePLStatus }));
    clearError('threePlStatus');
  };

  const setRoleType = (value: string) => {
    setFormValues((prev) => ({
      ...prev,
      roleType: value as LaborRoleType,
      // Clear VP zones when switching to a role type that doesn't support them (e.g. Fixed)
      // or when switching between DSD and Non-DSD (zones are different lists)
      vpZones: (value === 'DSD' || value === 'Non-DSD') ? [] : [],
    }));
    clearError('roleType');
  };

  const setVpZones = (values: string[]) => {
    setFormValues((prev) => ({ ...prev, vpZones: values }));
  };

  const handleStatusChange = (value: RoleStatus) => {
    setFormValues((prev) => ({ ...prev, status: value }));
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave(formValues);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedConfirm(true);
      return;
    }
    onCancel();
  };

  const availableInOptions: RoleAvailability[] = ['Observations', 'Labor Optimization'];

  const modalTitle = mode === 'add' ? 'Add New Role' : 'Edit Role';
  const primaryCta = mode === 'add' ? 'Save' : 'Save';

  return (
    <>
      <LHModalPop
        visible={visible}
        onClose={handleCancel}
        title={modalTitle}
        showFooter={true}
        primaryButtonText={primaryCta}
        secondaryButtonText="Cancel"
        onPrimaryPress={handleSave}
        onSecondaryPress={handleCancel}
        primaryButtonLoading={isSaving}
        primaryButtonDisabled={!canSave}
        secondaryButtonDisabled={isSaving}
        enableScroll={false}
        containerStyle={styles.modalContainer}
        titleStyle={styles.modalTitle}
      >
        <ScrollView
          style={styles.bodyScroll}
          contentContainerStyle={styles.bodyContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          {mode === 'edit' && (
            <View style={styles.headerToggleRow}>
              <Text style={[styles.headerToggleLabel, isDark && { color: colors.text.primary }]}>Status</Text>
              <ToggleSwitch value={formValues.status} onValueChange={handleStatusChange} disabled={isSaving} />
            </View>
          )}

          {mode === 'edit' && ((initialValues?.kfaReferenceCount ?? 0) > 0 || (initialValues?.activeLaborPlanCount ?? 0) > 0) && (
            <View style={[styles.infoBanner, isDark && { backgroundColor: '#0D2A4A', borderColor: '#1A4070' }]}>
              <MaterialIcons name="info-outline" size={16} color={isDark ? colors.brand.primary : '#005CBC'} />
              <Text style={[styles.infoBannerText, isDark && { color: colors.text.primary }]}>
                This role is referenced by {initialValues?.kfaReferenceCount ?? 0} Key Focus Areas and/or {initialValues?.activeLaborPlanCount ?? 0} active labor plans. Disabling or removing module availability will not alter existing references.
              </Text>
            </View>
          )}

          {/* ── Primary fields (2-col grid, full-width items span both) ── */}
          <View style={styles.formGrid}>
            {/* Role Name — full width */}
            <View style={styles.formGridFullWidth}>
              <FormSection title="Role Name" required error={errors.name}>
                <View style={errors.name ? styles.inputErrorBorder : undefined}>
                  <LhTextInput
                    placeholder="e.g., Forklift Operator"
                    value={formValues.name}
                    onChangeText={handleNameChange}
                    editable={!isSaving}
                    maxLength={15}
                  />
                </View>
              </FormSection>
            </View>

            {/* Available In — full width, inline checkboxes */}
            <View style={styles.formGridFullWidth}>
              <FormSection title="Available In" required error={errors.availableIn}>
                <View style={styles.checkboxRow}>
                  {availableInOptions.map((option) => (
                    <Checkbox
                      key={option}
                      label={option}
                      checked={formValues.availableIn.includes(option)}
                      onPress={() => toggleAvailableIn(option)}
                      disabled={isSaving}
                    />
                  ))}
                </View>
              </FormSection>
            </View>

            {/* Role Type — conditional, shown right after Available In */}
            {formValues.availableIn.includes('Labor Optimization') && (
              <View style={styles.formGridCol}>
                <SingleSelectDropdown
                  label="Role Type"
                  placeholder="Select role type"
                  options={roleTypeOptions}
                  value={formValues.roleType ?? ''}
                  required
                  error={errors.roleType}
                  disabled={isSaving}
                  onChange={setRoleType}
                />
              </View>
            )}

            {/* VP Zones — multiselect, shown for DSD and Non-DSD role types (case-insensitive check) */}
            {formValues.availableIn.includes('Labor Optimization') && (['DSD', 'NON-DSD'].includes((formValues.roleType ?? '').toUpperCase())) && (
              <View style={styles.formGridFullWidth}>
                <MultiSelectDropdown
                  label="VP Zones"
                  placeholder="Select VP zones"
                  options={vpZoneOptions}
                  selected={formValues.vpZones ?? []}
                  disabled={isSaving}
                  onChange={setVpZones}
                />
              </View>
            )}
          </View>

          {/* ── Scoping Attributes section divider ── */}
          <View style={[styles.sectionDivider, isDark && { borderBottomColor: colors.border.divider, marginTop: 10, marginBottom: 8 }]}>
            <Text style={[styles.sectionDividerText, isDark && { color: colors.text.secondary }]}>Scoping Attributes</Text>
          </View>

          {/* ── Scoping fields (2-col grid) ── */}
          <View style={styles.formGrid}>
            {/* Location Type + Organization Type — side by side */}
            <View style={styles.formGridCol}>
              <MultiSelectDropdown
                label="Location Type"
                placeholder="Select location types"
                options={locTypeOptions}
                selected={formValues.locTypes}
                required
                error={errors.locTypes}
                disabled={isSaving}
                onChange={setLocTypes}
              />
            </View>

            <View style={styles.formGridCol}>
              <MultiSelectDropdown
                label="Organization Type"
                placeholder="Select organization types"
                options={organizationTypeOptions}
                selected={formValues.organizationTypes}
                required
                error={errors.organizationTypes}
                disabled={isSaving}
                onChange={setOrganizationTypes}
              />
            </View>

            {/* 3PL Status — single column */}
            <View style={styles.formGridCol}>
              <SingleSelectDropdown
                label="3PL Status"
                placeholder="Select 3PL status"
                options={threePlStatusOptions}
                value={formValues.threePlStatus}
                required
                error={errors.threePlStatus}
                disabled={isSaving}
                onChange={setThreePlStatus}
              />
            </View>
          </View>

          {mode === 'add' && (
            <FormSection title="Status">
              <Text style={styles.statusHint}>Status will be set to Enabled on create.</Text>
            </FormSection>
          )}
        </ScrollView>
      </LHModalPop>

      <LHConfirmationDialog
        visible={showUnsavedConfirm}
        message="You have unsaved changes. Are you sure you want to discard them?"
        onConfirm={() => {
          setShowUnsavedConfirm(false);
          onCancel();
        }}
        onCancel={() => setShowUnsavedConfirm(false)}
      />

      <LHConfirmationDialog
        visible={showLaborRemovalConfirm}
        message="Removing Labor Optimization will clear its configuration fields. Continue?"
        onConfirm={() => {
          setFormValues((prev) => ({
            ...prev,
            availableIn: prev.availableIn.filter((item) => item !== 'Labor Optimization'),
            roleType: undefined,
            vpZones: [],
          }));
          setShowLaborRemovalConfirm(false);
        }}
        onCancel={() => setShowLaborRemovalConfirm(false)}
      />
    </>
  );
};

const styles = StyleSheet.create({
  modalContainer: {
    borderWidth: 1,
    borderColor: '#404040',
  },
  modalTitle: {
    fontFamily: FontFamily.bold,
  },
  bodyScroll: {
    maxHeight: 520,
  },
  bodyContent: {
    paddingTop: 8,
    paddingBottom: 24,
  },
  headerToggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  headerToggleLabel: {
    fontSize: 14,
    color: '#2D2D2D',
    fontFamily: FontFamily.medium,
  },
  formSection: {
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: FontFamily.medium,
    color: '#2D2D2D',
    lineHeight: 20,
  },
  requiredAsterisk: {
    fontSize: 14,
    color: '#D40020',
    marginLeft: 4,
    fontWeight: '700',
  },
  errorText: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#D40020',
    marginTop: 8,
    lineHeight: 16,
  },
  inputErrorBorder: {
    borderColor: '#D40020',
    borderWidth: 1,
    borderRadius: 6,
  },
  checkboxGroup: {
    gap: 12,
  },
  checkboxRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 4,
  },
  formGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 14,
  },
  formGridFullWidth: {
    width: '100%',
  },
  formGridCol: {
    flex: 1,
    minWidth: 200,
  },
  sectionDivider: {
    paddingBottom: 6,
    borderBottomWidth: 2,
    borderBottomColor: '#D7E5F8',
    marginTop: 18,
    marginBottom: 12,
  },
  sectionDividerText: {
    fontSize: 14,
    fontFamily: FontFamily.bold,
    color: '#2D2D2D',
    lineHeight: 18,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
  },
  checkboxBox: {
    width: 20,
    height: 20,
    borderWidth: 1,
    borderColor: '#B1B1B1',
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    backgroundColor: '#FFFFFF',
  },
  checkboxBoxChecked: {
    backgroundColor: '#005CBC',
    borderColor: '#005CBC',
  },
  checkboxBoxDisabled: {
    backgroundColor: '#F5F5F5',
    borderColor: '#C0C0C0',
  },
  checkboxLabel: {
    fontSize: 14,
    fontWeight: '400',
    color: '#2D2D2D',
    fontFamily: FontFamily.regular,
    lineHeight: 20,
  },
  checkboxLabelDisabled: {
    color: '#999999',
  },
  dropdownField: {
    marginTop: 0,
  },
  dropdownLabel: {
    fontSize: 14,
    fontFamily: FontFamily.medium,
    fontWeight: '500',
    color: '#2D2D2D',
    marginVertical: 0,
    marginBottom: 10,
  },
  dropdownPlaceholderText: {
    fontSize: 14,
    color: '#838383',
    fontFamily: FontFamily.regular,
  },
  dropdownTrigger: {
    minHeight: 40,
    borderWidth: 1,
    borderColor: '#C8D4E3',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#FFFFFF',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dropdownDisabled: {
    opacity: 0.6,
  },
  dropdownTriggerText: {
    fontSize: 14,
    color: '#2D2D2D',
    fontFamily: FontFamily.regular,
  },
  dropdownPlaceholder: {
    color: '#838383',
  },
  dropdownMenu: {
    marginTop: 8,
    borderWidth: 1,
    borderColor: '#D8E7FB',
    borderRadius: 6,
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
  },
  dropdownActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#EEF2F8',
  },
  dropdownActionText: {
    fontSize: 12,
    color: '#005CBC',
    fontFamily: FontFamily.medium,
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  optionText: {
    fontSize: 14,
    color: '#2D2D2D',
    fontFamily: FontFamily.regular,
  },
  tagWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  tag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#EAF3FF',
    borderColor: '#C7DDF9',
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  tagText: {
    fontSize: 12,
    color: '#01346F',
    fontFamily: FontFamily.medium,
  },
  radioOuter: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#7A7A7A',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioOuterActive: {
    borderColor: '#005CBC',
  },
  radioInner: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#005CBC',
  },
  infoBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    backgroundColor: '#EAF3FF',
    borderRadius: 6,
    padding: 10,
    marginBottom: 16,
  },
  infoBannerText: {
    flex: 1,
    fontSize: 12,
    lineHeight: 16,
    color: '#01346F',
    fontFamily: FontFamily.regular,
  },
  statusHint: {
    fontSize: 13,
    color: '#616161',
    fontFamily: FontFamily.regular,
    lineHeight: 18,
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  toggleTrack: {
    width: 50,
    height: 28,
    borderRadius: 14,
    padding: 2,
    justifyContent: 'center',
  },
  toggleTrackActive: {
    backgroundColor: '#4CAF50',
  },
  toggleTrackInactive: {
    backgroundColor: '#CCCCCC',
  },
  toggleTrackDisabled: {
    backgroundColor: '#E0E0E0',
  },
  toggleThumb: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
  },
  toggleThumbActive: {
    alignSelf: 'flex-end',
  },
  toggleThumbInactive: {
    alignSelf: 'flex-start',
  },
  toggleLabel: {
    fontSize: 14,
    fontWeight: '400',
    color: '#2D2D2D',
    fontFamily: FontFamily.regular,
    lineHeight: 20,
  },

  // ── Multi-select dropdown (custom per AC) ─────────────────────────────────
  multiTrigger: {
    minHeight: 40,
    borderWidth: 1,
    borderColor: '#C8D4E3',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#FFFFFF',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  multiTriggerDisabled: {
    opacity: 0.6,
    backgroundColor: '#F8F9FA',
  },
  multiTriggerError: {
    borderColor: '#D40020',
  },
  multiTriggerText: {
    fontSize: 14,
    color: '#2D2D2D',
    fontFamily: FontFamily.regular,
  },
  multiTriggerPlaceholder: {
    color: '#838383',
  },
  removableTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#EAF3FF',
    borderColor: '#C7DDF9',
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  removableTagText: {
    fontSize: 12,
    color: '#01346F',
    fontFamily: FontFamily.medium,
  },
  msOverlay: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  msDropdown: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D7E5F8',
    borderRadius: 8,
    paddingVertical: 4,
    shadowColor: '#3A3A3A',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 16,
    elevation: 12,
  },
  msActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#EEF2F8',
  },
  msActionText: {
    fontSize: 12,
    color: '#005CBC',
    fontFamily: FontFamily.medium,
  },
  msOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  msOptionText: {
    fontSize: 14,
    color: '#2D2D2D',
    fontFamily: FontFamily.regular,
  },
  msCheckbox: {
    width: 16,
    height: 16,
    borderWidth: 1.5,
    borderColor: '#C0C0C0',
    borderRadius: 3,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  msCheckboxChecked: {
    backgroundColor: '#005CBC',
    borderColor: '#005CBC',
  },
  msOptionsList: {
    maxHeight: 220,
  },
});

export default RoleFormModal;

